/**
 * callResults — Service-Layer submit for a prequal/calling call result.
 *
 * FR-FE-WRITE-PATH-ASSURANCE-2026-07-12 (MO-FE-WRITE-PATH-ASSURANCE). The
 * `POST /v1/calling/calls` INSERT used to travel react-query's in-memory
 * `retry: 1` ONLY — a tab close, reload, or dead battery between the button
 * press and the server ack lost the call_result with no durable copy (the same
 * data-loss shape as BUG-2026-07-14-VC559 on the visit-report path). The write
 * now travels the PACT durable-outbox (`durableSubmit`) like every other
 * critical write; this module is the SINGLE SL call the offline queue handler
 * replays, so the request shape lives in ONE place (page submit + queue replay
 * agree on the exact wire body).
 *
 * The replayed payload is the FLAT, already-built /v1/calling/calls body
 * (buildV2Body / buildLegacyBody in submitBodyBuilders.ts). This module is
 * transport-only and does NOT re-shape it — replaying it verbatim re-POSTs the
 * same columns the online attempt sent (call_timestamp captured at submit time,
 * never re-stamped to replay time).
 */
import { serviceFetch } from '../http/serviceLayerClient'

/**
 * The queued/replayed call-result payload: the already-built flat
 * `/v1/calling/calls` request body. Typed as a record because the column shape
 * is owned by the app layer (submitBodyBuilders.ts) — lib stays transport-only.
 */
export type CallResultQueuePayload = Record<string, unknown>

/**
 * POST the pre-built call-result body to the SL calls endpoint. Throws the raw
 * ServiceHttpError on failure so the replay loop / `shouldQueueFailedWrite` can
 * route a transient/5xx into a retry and a genuine 400/422 into a hard failure.
 */
export async function submitCallResultBody(body: CallResultQueuePayload): Promise<void> {
  await serviceFetch<unknown>('/v1/calling/calls', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}
